import { defineRouting } from "next-intl/routing";

export const locales = ["en", "ko", "ja", "zh-CN", "zh-TW"] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = "ko";

/**
 * 공개 언어 — 언어 선택기·sitemap·hreflang·언어 제안 배너에 노출되는 언어.
 * 새 언어는 먼저 `locales`에만 넣어 숨김 출시(URL 은 열리되 노출·색인 안 됨)하고,
 * 검수가 끝나면 여기에 추가한다. 순서는 `locales`를 따른다.
 */
const PUBLIC_LOCALE_SET: ReadonlySet<Locale> = new Set<Locale>(locales);
export const publicLocales: readonly Locale[] = locales.filter((l) => PUBLIC_LOCALE_SET.has(l));

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (locales as readonly string[]).includes(value);
}

export function isPublicLocale(value: unknown): value is Locale {
  return isLocale(value) && PUBLIC_LOCALE_SET.has(value);
}

/** 정규식 조각 — 경로 앞 locale 매칭용 (예: new RegExp(`^/(${LOCALE_PATTERN})(/|$)`)) */
export const LOCALE_PATTERN = locales
  .map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
  .join("|");

export const routing = defineRouting({
  locales,
  defaultLocale,
  localePrefix: "always",
  // Off: locale-prefix-less URLs always redirect to defaultLocale (deterministic destination
  // for Googlebot regardless of Accept-Language). Without this, the same URL bounces to
  // different locales per request, which Search Console flags as "Page with redirect" noise.
  localeDetection: false,
  // Off: next-intl would otherwise send an HTTP `Link` hreflang header on every
  // response that contradicts the HTML/sitemap hreflang (x-default = the
  // prefix-less redirecting URL, search landings without their query, all 5
  // locales even for unpublished blog translations or noindex pages). The HTML
  // (generateMetadata) and the sitemap already declare hreflang correctly.
  alternateLinks: false,
});
